import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Input, Select, Pagination, Spin, Empty, Button, Badge, Slider, Checkbox, Divider, Radio } from 'antd';
import { SearchOutlined, FilterOutlined, ShoppingOutlined, EyeOutlined, FireOutlined, StarOutlined, ThunderboltOutlined, ArrowLeftOutlined, ClearOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { getProducts, getMainCategories, getSubCategories } from '../services/productApi';
import { BaseUrlAddress } from '../components/Initilization';
import "../assets/css/productlist.css"

const { Search } = Input;
const { Option } = Select;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 1000000]);
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(1000000);
  const [filters, setFilters] = useState({
    search: '',
    main_category: null,
    sub_category: null,
    is_available: null,
    is_featured: null,
    is_new: null,
    is_best_seller: null,
    product_type: 'orderable', // 'all', 'orderable', 'designable'
    min_price: null,
    max_price: null,
    ordering: '-created',
    page: 1,
    page_size: 12
  });
  const [pagination, setPagination] = useState({ total: 0, current: 1 });
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 992); // 992px is the breakpoint for lg
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [filters]);

  useEffect(() => {
    // Fetch subcategories when main category changes
    if (filters.main_category) {
      fetchSubCategories(filters.main_category);
    } else {
      setSubCategories([]);
    }
  }, [filters.main_category]);

  const fetchCategories = async () => {
    try {
      const response = await getMainCategories();
      setCategories(response.data.ObjList || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await getSubCategories(categoryId);
      setSubCategories(response.data.ObjList || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.main_category) params.main_category = filters.main_category;
      if (filters.sub_category) params.sub_category = filters.sub_category;
      if (filters.is_available !== null) params.is_available = filters.is_available;
      if (filters.is_featured !== null) params.is_featured = filters.is_featured;
      if (filters.is_new !== null) params.is_new = filters.is_new;
      if (filters.is_best_seller !== null) params.is_best_seller = filters.is_best_seller;
      
      // Handle product_type filter
      if (filters.product_type === 'orderable') {
        params.is_orderable = true;
      } else if (filters.product_type === 'designable') {
        params.is_orderable = false;
      }
      // if 'all', don't send is_orderable filter
      
      if (filters.min_price) params.min_price = filters.min_price;
      if (filters.max_price) params.max_price = filters.max_price;
      if (filters.ordering) params.ordering = filters.ordering;
      params.page = filters.page;
      params.page_size = filters.page_size;
      
      const response = await getProducts(params);
      
      let productList = [];
      let totalCount = 0;
      
      if (response.data?.results) {
        productList = response.data.results;
        totalCount = response.data.count;
      } else if (response.data?.ObjList) {
        productList = response.data.ObjList;
        totalCount = response.data.ObjList.length;
      } else {
        productList = response.data || [];
        totalCount = productList.length;
      }
      
      setProducts(productList);
      setPagination({
        total: totalCount,
        current: filters.page
      });
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setFilters({ ...filters, search: value, page: 1 });
  };

  const handleCategoryChange = (value) => {
    setFilters({ ...filters, main_category: value, sub_category: null, page: 1 });
  };

  const handleSubCategoryChange = (value) => {
    setFilters({ ...filters, sub_category: value, page: 1 });
  };

  const handleSortChange = (value) => {
    setFilters({ ...filters, ordering: value });
  };

  const handlePageChange = (page) => {
    setFilters({ ...filters, page });
    window.scrollTo(0, 0);
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handlePriceChange = (value) => {
    setPriceRange(value);
    setFilters({ 
      ...filters, 
      min_price: value[0], 
      max_price: value[1],
      page: 1 
    });
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      main_category: null,
      sub_category: null,
      is_available: null,
      is_featured: null,
      is_new: null,
      is_best_seller: null,
      product_type: 'all',
      min_price: null,
      max_price: null,
      ordering: '-created',
      page: 1,
      page_size: 12
    });
    setPriceRange([0, 1000000]);
    setSubCategories([]);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.main_category) count++;
    if (filters.sub_category) count++;
    if (filters.is_available !== null) count++;
    if (filters.is_featured !== null) count++;
    if (filters.is_new !== null) count++;
    if (filters.is_best_seller !== null) count++;
    if (filters.product_type !== 'all') count++;
    if (filters.min_price) count++;
    if (filters.max_price) count++;
    return count;
  };

  const getBadgeStyle = (type) => {
    switch(type) {
      case 'discount':
        return {
          background: 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(255,65,108,0.3)'
        };
      case 'new':
        return {
          background: 'linear-gradient(135deg, #00b4db 0%, #0083b0 100%)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(0,180,219,0.3)'
        };
      case 'bestseller':
        return {
          background: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(245,175,25,0.3)'
        };
      case 'featured':
        return {
          background: 'linear-gradient(135deg, #a23854 0%, #c74b6e 100%)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(162,56,84,0.3)'
        };
      default:
        return {
          background: '#a23854',
          color: 'white'
        };
    }
  };

  const getCategoryIcon = (categoryName) => {
    if (categoryName?.includes('مردانه')) return <span>👔</span>;
    if (categoryName?.includes('زنانه')) return <span>👗</span>;
    if (categoryName?.includes('پسر')) return <span>👦</span>;
    if (categoryName?.includes('دختر')) return <span>👧</span>;
    return <ShoppingOutlined />;
  };

  // Mobile filter drawer toggle
  const toggleFilter = () => {
    setIsFilterExpanded(!isFilterExpanded);
  };

  // Filter Sidebar Component
  const FilterSidebar = () => (
    <div 
      className="product-card light-glass-effect-card shaped-border"
      style={{ 
        background: 'rgba(255,255,255,0.1)', 
        backdropFilter: 'blur(10px)',
        borderRadius: 20,
        padding: '20px',
        border: '1px solid rgba(162,56,84,0.2)',
        position: 'sticky',
        top: 20
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ color: '#a23854', margin: 0 }}>فیلترها</h3>
        {getActiveFilterCount() > 0 && (
          <Button type="secondary" onClick={resetFilters} size="small" icon={<ClearOutlined />}>
            حذف فیلتر ها
          </Button>
        )}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8, fontSize: 13, color: '#a23854', fontWeight: 500 }}>جستجو</div>
        <Search
          style={{
            paddingTop: "0px",
            height: "35px"
          }}
          placeholder="نام محصول..."
          allowClear
          onSearch={handleSearch}
          size="middle"
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
        />
      </div>

      <Divider style={{ margin: '16px 0', borderColor: 'rgba(162,56,84,0.3)' }} />

      {/* Price Range Slider */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8, fontSize: 13, color: '#a23854', fontWeight: 500 }}>محدوده قیمت (تومان)</div>
        <Slider
          range
          min={0}
          max={1000000}
          step={10000}
          value={priceRange}
          onChange={handlePriceChange}
          tooltip={{ formatter: (value) => `${value?.toLocaleString()} تومان` }}
          style={{ direction: 'ltr' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <span style={{ fontSize: 12, color: '#666' }}>{priceRange[0].toLocaleString()}</span>
          <span style={{ fontSize: 12, color: '#666' }}>{priceRange[1].toLocaleString()}</span>
        </div>
      </div>

      <Divider style={{ margin: '16px 0', borderColor: 'rgba(162,56,84,0.3)' }} />

      {/* Categories */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8, fontSize: 13, color: '#a23854', fontWeight: 500 }}>دسته بندی</div>
        <Select
          placeholder="دسته اصلی"
          allowClear
          value={filters.main_category}
          onChange={handleCategoryChange}
          style={{ width: '100%', marginBottom: 12 }}
        >
          {categories.map(cat => (
            <Option key={cat.id} value={cat.id}>{cat.name_fa}</Option>
          ))}
        </Select>
        <Select
          placeholder="زیردسته"
          allowClear
          value={filters.sub_category}
          onChange={handleSubCategoryChange}
          style={{ width: '100%' }}
          disabled={!filters.main_category}
        >
          {subCategories.map(sub => (
            <Option key={sub.id} value={sub.id}>{sub.name_fa}</Option>
          ))}
        </Select>
      </div>

      <Divider style={{ margin: '16px 0', borderColor: 'rgba(162,56,84,0.3)' }} />

      {/* Product Type - Radio Group */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 12, fontSize: 13, color: '#a23854', fontWeight: 500 }}>نوع محصول</div>
        <Radio.Group 
          value={filters.product_type} 
          onChange={(e) => handleFilterChange('product_type', e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <Radio value="all">همه محصولات</Radio>
          <Radio value="orderable">محصولات قابل سفارش</Radio>
          <Radio value="designable">محصولات قابل طراحی</Radio>
        </Radio.Group>
      </div>

      <Divider style={{ margin: '16px 0', borderColor: 'rgba(162,56,84,0.3)' }} />

      {/* Product Status */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 12, fontSize: 13, color: '#a23854', fontWeight: 500 }}>وضعیت محصول</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Checkbox 
            checked={filters.is_available === true}
            onChange={(e) => handleFilterChange('is_available', e.target.checked ? true : null)}
          >
            فقط محصولات موجود
          </Checkbox>
          <Checkbox 
            checked={filters.is_featured === true}
            onChange={(e) => handleFilterChange('is_featured', e.target.checked ? true : null)}
          >
            محصولات ویژه
          </Checkbox>
          <Checkbox 
            checked={filters.is_new === true}
            onChange={(e) => handleFilterChange('is_new', e.target.checked ? true : null)}
          >
            محصولات جدید
          </Checkbox>
          <Checkbox 
            checked={filters.is_best_seller === true}
            onChange={(e) => handleFilterChange('is_best_seller', e.target.checked ? true : null)}
          >
            پرفروش‌ترین‌ها
          </Checkbox>
        </div>
      </div>

      <Divider style={{ margin: '16px 0', borderColor: 'rgba(162,56,84,0.3)' }} />

      {/* Sort Order */}
      <div>
        <div style={{ marginBottom: 8, fontSize: 13, color: '#a23854', fontWeight: 500 }}>مرتب سازی</div>
        <Select
          value={filters.ordering}
          onChange={handleSortChange}
          style={{ width: '100%' }}
        >
          <Option value="-created">جدیدترین</Option>
          <Option value="created">قدیمی‌ترین</Option>
          <Option value="-base_price">گران‌ترین</Option>
          <Option value="base_price">ارزان‌ترین</Option>
          <Option value="-rating">محبوب‌ترین</Option>
        </Select>
      </div>

      {/* Active Filters Summary */}
      {getActiveFilterCount() > 0 && (
        <>
          <Divider style={{ margin: '16px 0', borderColor: 'rgba(162,56,84,0.3)' }} />
          <div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
              فیلترهای فعال: {getActiveFilterCount()}
            </div>
            <Button 
              type="primary" 
              onClick={applyFilters} 
              style={{ width: '100%', background: '#a23854', borderColor: '#a23854' }}
            >
              اعمال فیلترها
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const applyFilters = () => {
    fetchProducts();
  };

  return (
    <div style={{ direction: 'rtl', maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
      
      {/* Hero Section */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: 8, color: "white" }}>
          محصولات
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.7)' }}>
          جدیدترین و پرطرفدارترین محصولات چاپ سفارشی را در آقای چاپ پیدا کنید
        </p>
      </div>
      
      {/* Mobile Filter Toggle - Only visible on mobile screens */}
      <div style={{ marginBottom: 16, display: isMobile ? 'block' : 'none' }}>
        <Button 
          icon={<FilterOutlined />} 
          onClick={toggleFilter}
          type='secondary'
          style={{ width: '100%'}}
        >
          فیلترها {getActiveFilterCount() > 0 && <Badge count={getActiveFilterCount()} style={{ backgroundColor: '#a23854', marginRight: 8 }} />}
        </Button>
      </div>

      {/* Main Content Row */}
      <Row gutter={[24, 24]}>
        {/* Left Sidebar - Filters (hidden on mobile) */}
        <Col xs={0} lg={6}>
          <FilterSidebar />
        </Col>

        {/* Mobile Filter Drawer */}
        {isMobile && isFilterExpanded && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(220,36,36,1) 6%, rgba(74,86,157,1) 97%)',
            zIndex: 1000,
            padding: 20,
            overflow: 'auto'
          }}>
            <div style={{
              background: 'linear-gradient(45deg, rgba(220,36,36,1) 6%, rgba(74,86,157,1) 97%)',
              borderRadius: 20,
              padding: 20,
              minHeight: '100%'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                <h3 style={{ color: '#a23854' }}>فیلترها</h3>
                <Button onClick={() => setIsFilterExpanded(false)}>بستن</Button>
              </div>
              <FilterSidebar />
            </div>
          </div>
        )}

        {/* Products Grid */}
        <Col xs={24} lg={18}>
          {/* Results Summary */}
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ 
              padding: '4px 12px', 
              background: 'rgba(162,56,84,0.1)', 
              borderRadius: 20,
              fontSize: 13,
              color: 'white'
            }}>
              {pagination.total.toLocaleString()} محصول
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'white' }}>مرتب سازی:</span>
              <Select
                value={filters.ordering}
                onChange={handleSortChange}
                size="small"
                style={{ width: 130 }}
              >
                <Option value="-created">جدیدترین</Option>
                <Option value="-base_price">گران‌ترین</Option>
                <Option value="base_price">ارزان‌ترین</Option>
                <Option value="-rating">محبوب‌ترین</Option>
              </Select>
            </div>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
              <Spin size="large" />
            </div>
          ) : products.length === 0 ? (
            <Empty description="محصولی یافت نشد" />
          ) : (
            <>
              <Row gutter={[16, 16]}>
                {products.map(product => (
                  <Col xs={12} sm={12}  lg={8} key={product.id}>
                    <Link to={`/product/${product.slug}`}>
                      <Card
                        hoverable
                        className="product-card light-glass-effect-card shaped-border"
                        style={{
                          textDecorationLine: "none",
                          borderRadius: 16,
                          overflow: 'hidden',
                          background: 'rgba(255,255,255,0.3)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(162,56,84,0.15)',
                          transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                        }}
                        bodyStyle={{ padding: '12px' }}
                        cover={
                          <div style={{ 
                            height: 180, 
                            overflow: 'hidden', 
                            position: 'relative',
                            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
                          }}>
                            {/* Badges Container */}
                            <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, display: 'flex', gap: 6, flexDirection: 'column', alignItems: 'flex-start' }}>
                              {product.discount_percent > 0 && (
                                <div style={{
                                  ...getBadgeStyle('discount'),
                                  padding: '2px 8px',
                                  borderRadius: 16,
                                  fontSize: 9,
                                  fontWeight: 'bold'
                                }}>
                                  تخفیف {product.discount_percent}%
                                </div>
                              )}
                              {product.is_new && (
                                <div style={{
                                  ...getBadgeStyle('new'),
                                  padding: '2px 8px',
                                  borderRadius: 16,
                                  fontSize: 9,
                                  fontWeight: 'bold'
                                }}>
                                  جدید
                                </div>
                              )}
                            </div>

                            {/* Product Image */}
                            <div style={{ 
                              height: '100%', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              padding: 12
                            }}>
                              {product.main_image_url ? (
                                <img 
                                src={(BaseUrlAddress + product.main_image_url).replace(/([^:]\/)\/+/g, '$1')}
                                  alt={product.title} 
                                  style={{ 
                                    maxHeight: '140px', 
                                    maxWidth: '100%', 
                                    objectFit: 'contain'
                                  }}
                                />
                              ) : (
                                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                                  <ShoppingOutlined style={{ fontSize: 32 }} />
                                </div>
                              )}
                            </div>
                          </div>
                        }
                      >
                        {/* Category Tag */}
                        <div style={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          marginBottom: 8,
                          padding: '2px 8px',
                          background: 'rgba(162,56,84,0.15)',
                          borderRadius: 16,
                          fontSize: 9,
                          color: '#a23854'
                        }}>
                          {getCategoryIcon(product.main_category_name)}
                          <span>{product.main_category_name}</span>
                        </div>

                        {/* Product Title */}
                        <h4 style={{
                          fontFamily: 'Yekan',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: 'black',
                          marginBottom: 6,
                          height: 38,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {product.title}
                        </h4>

                        {/* Price Section */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'baseline',
                          justifyContent: 'space-between',
                          paddingTop: 8,
                          borderTop: '1px solid rgba(0,0,0,0.3)'
                        }}>
                          <div>
                            {product.discount_percent > 0 ? (
                              <>
                                <div style={{ 
                                  fontSize: '1rem', 
                                  fontWeight: 'bold',
                                  color: '#a23854'
                                }}>
                                  {product.final_price.toLocaleString()}
                                </div>
                                <div style={{ 
                                  fontSize: '0.6rem', 
                                  color: 'rgba(100,30,30,0.5)',
                                  textDecoration: 'line-through'
                                }}>
                                  {product.base_price.toLocaleString()}
                                </div>
                              </>
                            ) : (
                              <div style={{ 
                                fontSize: '1rem', 
                                fontWeight: 'bold',
                                color: '#a23854'
                              }}>
                                {product.base_price.toLocaleString()}
                                <span style={{ fontSize: '0.6rem', marginRight: 2 }}>تومان</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Quick View Button */}
                          
                        </div>
                      </Card>
                    </Link>
                  </Col>
                ))}
              </Row>
              
              {/* Pagination */}
              {pagination.total > filters.page_size && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
                  <Pagination
                    current={pagination.current}
                    total={pagination.total}
                    pageSize={filters.page_size}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                  />
                </div>
              )}
            </>
          )}
        </Col>
      </Row>

      {/* Global CSS Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .ant-input-affix-wrapper{
          padding-top:0px;
          height:40px !important;
        }

          .product-card:hover {
            transform: translateY(-4px);
            border-color: rgba(162,56,84,0.4) !important;
            box-shadow: 0 10px 20px rgba(0,0,0,0.2) !important;
          }
          
          .ant-slider-track {
            background-color: #a23854 !important;
          }
          
          .ant-slider-handle {
            border-color: #a23854 !important;
          }
          
          .ant-slider-handle:hover {
            border-color: #c74b6e !important;
          }
          
          .ant-checkbox-checked .ant-checkbox-inner {
            background-color: #a23854 !important;
            border-color: #a23854 !important;
          }
          
          .ant-pagination-item-active {
            background: linear-gradient(135deg, #a23854, #c74b6e) !important;
            border-color: transparent !important;
          }
          
          .ant-pagination-item-active a {
            color: white !important;
          }
          
          .ant-pagination-item:hover {
            border-color: #a23854 !important;
          }
          
          .ant-pagination-item:hover a {
            color: #a23854 !important;
          }
          
          @media (max-width: 768px) {
            .product-card .ant-card-body {
              padding: 8px !important;
            }
          }
        `
      }} />
    </div>
  );
};

export default Products;